import { useState, useEffect } from 'react';
import Navigation from '../components/Navigation';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { 
  Trophy, Target, Flame, Moon, Brain, Footprints, Scale,
  Users, Clock, IndianRupee, TrendingUp, Award, Zap,
  ChevronRight, CheckCircle, Calendar, Star, Crown,
  Loader2, Plus, Medal
} from 'lucide-react';
import axios from 'axios';
import { API } from '../App';
import { toast } from 'sonner';

const CHALLENGE_ICONS = {
  steps: Footprints,
  weight_loss: Scale,
  sleep: Moon,
  active_minutes: Flame,
  meditation: Brain,
  hydration: Zap,
  custom: Target
};

const DIFFICULTY_COLORS = {
  beginner: "bg-green-500/20 text-green-400 border-green-500/30",
  intermediate: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  advanced: "bg-orange-500/20 text-orange-400 border-orange-500/30",
  extreme: "bg-red-500/20 text-red-400 border-red-500/30"
};

const HealthBet = ({ user, setUser }) => {
  const [activeTab, setActiveTab] = useState('browse');
  const [challenges, setChallenges] = useState([]);
  const [myChallenges, setMyChallenges] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [earnings, setEarnings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showJoinDialog, setShowJoinDialog] = useState(null);
  const [showProgressDialog, setShowProgressDialog] = useState(null);
  const [progressValue, setProgressValue] = useState('');
  const [joining, setJoining] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [templatesRes, challengesRes, myRes, earningsRes] = await Promise.all([
        axios.get(`${API}/healthbet/challenges/templates`),
        axios.get(`${API}/healthbet/challenges/active`),
        axios.get(`${API}/healthbet/challenges/my`),
        axios.get(`${API}/healthbet/stats/my-earnings`)
      ]);
      
      setTemplates(templatesRes.data.templates || []);
      setChallenges(challengesRes.data.challenges || []);
      setMyChallenges(myRes.data.challenges || []);
      setEarnings(earningsRes.data);
    } catch (error) {
      console.error('Error loading data:', error);
      // Try to seed demo data if no challenges exist
      try {
        await axios.post(`${API}/healthbet/challenges/seed-demo`);
        const challengesRes = await axios.get(`${API}/healthbet/challenges/active`);
        setChallenges(challengesRes.data.challenges || []);
      } catch (e) {
        console.error('Error seeding demo:', e);
      }
    } finally {
      setLoading(false);
    }
  };

  const joinChallenge = async (challenge) => {
    setJoining(true);
    try {
      const response = await axios.post(`${API}/healthbet/challenges/join`, {
        challenge_id: challenge.id,
        commitment_message: "I'm committed to completing this challenge!"
      });
      
      toast.success(`Joined "${challenge.name}"! Good luck! 🎉`);
      setShowJoinDialog(null);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to join challenge');
    } finally {
      setJoining(false);
    }
  };

  const logProgress = async () => {
    if (!progressValue || !showProgressDialog) return;
    
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await axios.post(`${API}/healthbet/challenges/log-progress`, {
        challenge_id: showProgressDialog.id,
        date: today,
        value: parseFloat(progressValue)
      });
      
      toast.success(response.data.message);
      setShowProgressDialog(null);
      setProgressValue('');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to log progress');
    }
  };

  const getChallengeIcon = (type) => {
    const Icon = CHALLENGE_ICONS[type] || Target;
    return Icon;
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0
    }).format(amount);
  };

  const getDaysRemaining = (endDate) => {
    const end = new Date(endDate);
    const now = new Date();
    const days = Math.ceil((end - now) / (1000 * 60 * 60 * 24));
    return Math.max(0, days);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
        <Navigation user={user} setUser={setUser} />
        <div className="container mx-auto px-4 py-8 flex items-center justify-center">
          <Loader2 className="w-8 h-8 animate-spin text-white" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      <Navigation user={user} setUser={setUser} />
      
      <main className="container mx-auto px-4 py-8">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-600">
              <Trophy className="w-6 h-6 text-white" />
            </div>
            <h1 className="text-3xl font-bold text-white">HealthBet</h1>
            <Badge className="bg-green-500/20 text-green-400 border-green-500/30">
              Win Money
            </Badge>
          </div>
          <p className="text-gray-400">Bet on your health goals. Achieve them. Win real money!</p>
        </div>

        {/* Stats Banner */}
        {earnings && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
            <Card className="bg-white/10 border-white/20 text-white">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Target className="w-4 h-4 text-blue-400" />
                  <span className="text-xs text-gray-400">Challenges</span>
                </div>
                <p className="text-2xl font-bold">{earnings.total_challenges}</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-white/20 text-white">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Trophy className="w-4 h-4 text-yellow-400" />
                  <span className="text-xs text-gray-400">Win Rate</span>
                </div>
                <p className="text-2xl font-bold">{earnings.win_rate}%</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-white/20 text-white">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <IndianRupee className="w-4 h-4 text-green-400" />
                  <span className="text-xs text-gray-400">Total Won</span>
                </div>
                <p className="text-2xl font-bold">{formatCurrency(earnings.total_won)}</p>
              </CardContent>
            </Card>
            <Card className="bg-white/10 border-white/20 text-white">
              <CardContent className="pt-4 pb-4">
                <div className="flex items-center gap-2 mb-1">
                  <Flame className="w-4 h-4 text-orange-400" />
                  <span className="text-xs text-gray-400">Best Streak</span>
                </div>
                <p className="text-2xl font-bold">{earnings.best_streak} days</p>
              </CardContent>
            </Card>
          </div>
        )}

        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="bg-white/10 border-white/20">
            <TabsTrigger value="browse" className="data-[state=active]:bg-white/20">
              <Trophy className="w-4 h-4 mr-2" />
              Browse Challenges
            </TabsTrigger>
            <TabsTrigger value="my" className="data-[state=active]:bg-white/20">
              <Target className="w-4 h-4 mr-2" />
              My Challenges
            </TabsTrigger>
            <TabsTrigger value="leaderboard" className="data-[state=active]:bg-white/20">
              <Crown className="w-4 h-4 mr-2" />
              Leaderboard
            </TabsTrigger>
          </TabsList>

          {/* Browse Challenges Tab */}
          <TabsContent value="browse" className="space-y-6">
            {/* Active Challenges */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Zap className="w-5 h-5 text-yellow-400" />
                Active Challenges
              </h2>
              
              {challenges.length === 0 ? (
                <Card className="bg-white/10 border-white/20 text-white">
                  <CardContent className="py-8 text-center">
                    <Trophy className="w-12 h-12 mx-auto text-gray-500 mb-4" />
                    <p className="text-gray-400">No active challenges yet. Check back soon!</p>
                  </CardContent>
                </Card>
              ) : (
                <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                  {challenges.map((challenge) => {
                    const Icon = getChallengeIcon(challenge.challenge_type);
                    const daysLeft = getDaysRemaining(challenge.end_date);
                    
                    return (
                      <Card 
                        key={challenge.id} 
                        className="bg-white/10 border-white/20 text-white hover:border-white/40 transition-colors"
                      >
                        <CardHeader className="pb-2">
                          <div className="flex justify-between items-start">
                            <div className="flex items-center gap-2">
                              <div className="p-2 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-600">
                                <Icon className="w-5 h-5 text-white" />
                              </div>
                              <div>
                                <CardTitle className="text-base">{challenge.name}</CardTitle>
                                <Badge className={`text-xs mt-1 ${DIFFICULTY_COLORS[challenge.difficulty]}`}>
                                  {challenge.difficulty}
                                </Badge>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <p className="text-sm text-gray-400 line-clamp-2">{challenge.description}</p>
                          
                          <div className="grid grid-cols-2 gap-2 text-sm">
                            <div className="flex items-center gap-1 text-gray-300">
                              <IndianRupee className="w-4 h-4 text-green-400" />
                              <span>Entry: {formatCurrency(challenge.entry_fee)}</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-300">
                              <Users className="w-4 h-4 text-blue-400" />
                              <span>{challenge.participants_count} joined</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-300">
                              <Clock className="w-4 h-4 text-purple-400" />
                              <span>{challenge.duration_days} days</span>
                            </div>
                            <div className="flex items-center gap-1 text-gray-300">
                              <Trophy className="w-4 h-4 text-yellow-400" />
                              <span>Pot: {formatCurrency(challenge.total_pot)}</span>
                            </div>
                          </div>

                          <div className="pt-2 border-t border-white/10">
                            {challenge.user_joined ? (
                              <Button 
                                className="w-full bg-green-600/50" 
                                disabled
                              >
                                <CheckCircle className="w-4 h-4 mr-2" />
                                Already Joined
                              </Button>
                            ) : (
                              <Button 
                                className="w-full bg-gradient-to-r from-yellow-500 to-orange-600 hover:from-yellow-600 hover:to-orange-700"
                                onClick={() => setShowJoinDialog(challenge)}
                              >
                                Join Challenge
                                <ChevronRight className="w-4 h-4 ml-1" />
                              </Button>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Challenge Templates */}
            <div>
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Star className="w-5 h-5 text-purple-400" />
                Challenge Types
              </h2>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                {templates.slice(0, 5).map((template) => {
                  const Icon = getChallengeIcon(template.challenge_type);
                  return (
                    <Card 
                      key={template.id}
                      className="bg-white/5 border-white/10 text-white hover:bg-white/10 transition-colors cursor-pointer"
                    >
                      <CardContent className="pt-4 pb-4 text-center">
                        <div className="w-12 h-12 mx-auto rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center mb-2">
                          <Icon className="w-6 h-6 text-white" />
                        </div>
                        <p className="text-sm font-medium">{template.name.split(' ').slice(1).join(' ')}</p>
                        <p className="text-xs text-gray-400 mt-1">{formatCurrency(template.entry_fee)}</p>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            </div>
          </TabsContent>

          {/* My Challenges Tab */}
          <TabsContent value="my" className="space-y-6">
            {myChallenges.length === 0 ? (
              <Card className="bg-white/10 border-white/20 text-white">
                <CardContent className="py-12 text-center">
                  <Target className="w-16 h-16 mx-auto text-gray-500 mb-4" />
                  <h3 className="text-xl font-semibold mb-2">No Active Challenges</h3>
                  <p className="text-gray-400 mb-4">Join a challenge to start earning!</p>
                  <Button 
                    onClick={() => setActiveTab('browse')}
                    className="bg-gradient-to-r from-yellow-500 to-orange-600"
                  >
                    Browse Challenges
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {myChallenges.map((challenge) => {
                  const Icon = getChallengeIcon(challenge.challenge_type);
                  const daysLeft = getDaysRemaining(challenge.end_date);
                  const participation = challenge.participation || {};
                  
                  return (
                    <Card key={challenge.id} className="bg-white/10 border-white/20 text-white">
                      <CardContent className="pt-6">
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                          <div className="flex items-center gap-4">
                            <div className="p-3 rounded-xl bg-gradient-to-br from-yellow-500 to-orange-600">
                              <Icon className="w-6 h-6 text-white" />
                            </div>
                            <div>
                              <h3 className="font-semibold text-lg">{challenge.name}</h3>
                              <div className="flex items-center gap-3 text-sm text-gray-400">
                                <span>{daysLeft} days left</span>
                                <span>•</span>
                                <span>Streak: {participation.current_streak || 0} days 🔥</span>
                              </div>
                            </div>
                          </div>
                          
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-2xl font-bold text-green-400">
                                {participation.completion_percentage || 0}%
                              </p>
                              <p className="text-xs text-gray-400">completed</p>
                            </div>
                            <Button
                              onClick={() => setShowProgressDialog(challenge)}
                              className="bg-teal-600 hover:bg-teal-700"
                            >
                              <Plus className="w-4 h-4 mr-1" />
                              Log Progress
                            </Button>
                          </div>
                        </div>
                        
                        <div className="mt-4">
                          <div className="flex justify-between text-sm mb-1">
                            <span className="text-gray-400">Progress</span>
                            <span className="text-gray-400">
                              {participation.days_completed || 0}/{challenge.duration_days} days
                            </span>
                          </div>
                          <Progress value={participation.completion_percentage || 0} className="h-2" />
                        </div>
                        
                        <div className="mt-4 pt-4 border-t border-white/10 flex justify-between items-center">
                          <div className="text-sm">
                            <span className="text-gray-400">Daily Goal: </span>
                            <span className="text-white font-medium">
                              {challenge.daily_goal.toLocaleString()} {challenge.challenge_type === 'steps' ? 'steps' : 
                                challenge.challenge_type === 'sleep' ? 'hours' : 
                                challenge.challenge_type === 'active_minutes' ? 'mins' : 'units'}
                            </span>
                          </div>
                          <div className="text-sm">
                            <span className="text-gray-400">Potential Win: </span>
                            <span className="text-green-400 font-medium">
                              {formatCurrency(challenge.total_pot * 0.9 / Math.max(1, Math.ceil(challenge.participants_count * 0.5)))}
                            </span>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>
            )}
          </TabsContent>

          {/* Leaderboard Tab */}
          <TabsContent value="leaderboard" className="space-y-6">
            <Card className="bg-white/10 border-white/20 text-white">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Crown className="w-5 h-5 text-yellow-400" />
                  Top Performers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {[
                    { rank: 1, name: "Rahul K.", challenges: 12, winnings: 15000, streak: 45 },
                    { rank: 2, name: "Priya S.", challenges: 8, winnings: 12500, streak: 38 },
                    { rank: 3, name: "Amit P.", challenges: 15, winnings: 11000, streak: 32 },
                    { rank: 4, name: "Sneha M.", challenges: 6, winnings: 8500, streak: 28 },
                    { rank: 5, name: "Vikram R.", challenges: 10, winnings: 7200, streak: 25 },
                  ].map((player, idx) => (
                    <div 
                      key={`item-${idx}`}
                      className={`flex items-center justify-between p-3 rounded-lg ${
                        idx === 0 ? 'bg-yellow-500/20 border border-yellow-500/30' :
                        idx === 1 ? 'bg-gray-400/20 border border-gray-400/30' :
                        idx === 2 ? 'bg-orange-600/20 border border-orange-600/30' :
                        'bg-white/5'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-bold ${
                          idx === 0 ? 'bg-yellow-500 text-black' :
                          idx === 1 ? 'bg-gray-400 text-black' :
                          idx === 2 ? 'bg-orange-600 text-white' :
                          'bg-white/10 text-white'
                        }`}>
                          {player.rank}
                        </div>
                        <div>
                          <p className="font-medium">{player.name}</p>
                          <p className="text-xs text-gray-400">{player.challenges} challenges</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="font-medium text-green-400">{formatCurrency(player.winnings)}</p>
                        <p className="text-xs text-gray-400">{player.streak} day streak</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>

      {/* Join Challenge Dialog */}
      <Dialog open={!!showJoinDialog} onOpenChange={() => setShowJoinDialog(null)}>
        <DialogContent className="bg-slate-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Trophy className="w-5 h-5 text-yellow-400" />
              Join {showJoinDialog?.name}
            </DialogTitle>
            <DialogDescription className="text-gray-400">
              Pay the entry fee and commit to achieving your goal!
            </DialogDescription>
          </DialogHeader>
          
          {showJoinDialog && (
            <div className="space-y-4 pt-4">
              <div className="bg-white/5 rounded-lg p-4 space-y-2">
                <div className="flex justify-between">
                  <span className="text-gray-400">Entry Fee</span>
                  <span className="font-bold text-green-400">{formatCurrency(showJoinDialog.entry_fee)}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Duration</span>
                  <span>{showJoinDialog.duration_days} days</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Daily Goal</span>
                  <span>{showJoinDialog.daily_goal.toLocaleString()}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Current Pot</span>
                  <span className="text-yellow-400">{formatCurrency(showJoinDialog.total_pot)}</span>
                </div>
              </div>
              
              <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
                <p className="text-sm text-yellow-300">
                  💡 Complete the challenge to split the pot with other winners. 
                  Platform takes 10% fee.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-white/20"
                  onClick={() => setShowJoinDialog(null)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-gradient-to-r from-yellow-500 to-orange-600"
                  onClick={() => joinChallenge(showJoinDialog)}
                  disabled={joining}
                >
                  {joining ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <IndianRupee className="w-4 h-4 mr-1" />
                  )}
                  Pay & Join
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Log Progress Dialog */}
      <Dialog open={!!showProgressDialog} onOpenChange={() => setShowProgressDialog(null)}>
        <DialogContent className="bg-slate-900 border-white/20 text-white">
          <DialogHeader>
            <DialogTitle>Log Today's Progress</DialogTitle>
            <DialogDescription className="text-gray-400">
              {showProgressDialog?.name}
            </DialogDescription>
          </DialogHeader>
          
          {showProgressDialog && (
            <div className="space-y-4 pt-4">
              <div>
                <Label>
                  {showProgressDialog.challenge_type === 'steps' ? 'Steps Today' :
                   showProgressDialog.challenge_type === 'sleep' ? 'Hours Slept' :
                   showProgressDialog.challenge_type === 'weight_loss' ? 'Current Weight (kg)' :
                   showProgressDialog.challenge_type === 'active_minutes' ? 'Active Minutes' :
                   'Value'}
                </Label>
                <Input
                  type="number"
                  value={progressValue}
                  onChange={(e) => setProgressValue(e.target.value)}
                  placeholder={`Goal: ${showProgressDialog.daily_goal}`}
                  className="bg-white/10 border-white/20 mt-2"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Daily goal: {showProgressDialog.daily_goal.toLocaleString()}
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  variant="outline"
                  className="flex-1 border-white/20"
                  onClick={() => {
                    setShowProgressDialog(null);
                    setProgressValue('');
                  }}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-teal-600 hover:bg-teal-700"
                  onClick={logProgress}
                  disabled={!progressValue}
                >
                  <CheckCircle className="w-4 h-4 mr-2" />
                  Log Progress
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default HealthBet;
